import React, { useEffect, useState, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

// Material UI
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import Checkbox from '@material-ui/core/Checkbox';
import Tooltip from '@material-ui/core/Tooltip';
import EditIcon from '@material-ui/icons/Edit';
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import AssessmentIcon from '@material-ui/icons/Assessment';
import Modal from '@material-ui/core/Modal';

// Redux
import { useSelector, useDispatch } from 'react-redux';
import { RootStore } from '../../../Store';
import { 
  getPreorderEventDetails, 
  archivePreorderEvent,
  updatePreorderEventDetails,
  updatePreorderItemQuantity,
  updatePreorderItemDetails,
  getRecommendedClosedPreorder,
  closePreorderEvent,
  clearSuggestedPreorderCloseDistribution,
  bulkPublishPreorderItems
} from '../../../actions/preorderEventActions/PreorderEventActions';
import { PreorderCloseEventRequest } from '../../../actions/preorderEventActions/PreorderEventActionsTypes';

// Components
import ConfirmationDialog from './ConfirmationDialog';
import UpdatePreorderDetailsDialog from './UpdatePreorderDetailsDialog';
import AddItemsDialog from './AddItemsDialog';
import EditItemDialog from './EditItemDialog';
import EditQuantityDialog from './EditQuantityDialog';
import EditPriceDialog from './EditPriceDialog';
import ActionAlert from '../utils/ActionAlert';

// Moment for date formatting
import Moment from 'moment';

// Translations
import { useTranslation } from 'react-i18next';

// Styles
import useStyles from './PreorderEventDetailsStyles';
import { PreorderSuggestedDistributionModal } from './PreorderSuggestedDistributionModal';
import PreorderMetricsPrintButton from './PreorderMetricsPrintButton';

const PreorderEventDetails = () => {
  const classes = useStyles();
  const { t } = useTranslation('global');
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  
  const urlParams = new URLSearchParams(location.search);
  const preorderId = urlParams.get('preorderId');

  const preorderEventState = useSelector((state: RootStore) => state.preorderEvents);
  const { preorderEventDetails, loading, suggestedPreorderCloseDistribution, actionStatus } = preorderEventState;

  // Multi vendor selection (empty array means ALL vendors aggregated)
  const [selectedVendorIds, setSelectedVendorIds] = useState<number[]>([]);
  const [hasInitializedVendors, setHasInitializedVendors] = useState(false);

  // Item selection for metrics filtering (mutually exclusive with vendor selection)
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
    confirmText: ''
  });

  // Removed location selection; vendor chips will occupy that UI space.

  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [addItemsDialogOpen, setAddItemsDialogOpen] = useState(false);
  const [editItemDialogOpen, setEditItemDialogOpen] = useState(false);
  const [editQuantityDialogOpen, setEditQuantityDialogOpen] = useState(false);
  const [editPriceDialogOpen, setEditPriceDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemMetricsModalOpen, setItemMetricsModalOpen] = useState(false);
  const [metricsVendorFilter, setMetricsVendorFilter] = useState<number | null>(null);

  useEffect(() => {
    if (preorderId) {
      dispatch(getPreorderEventDetails(parseInt(preorderId)));
    }
  }, [dispatch, preorderId]);

  // Redirect to preorder events list after successful close or archive
  useEffect(() => {
    if (actionStatus?.status === 'success') {
      if (actionStatus?.menssage === 'status.close-success' || actionStatus?.menssage === 'status.success-archive') {
        history.push('/admin/preorder-events');
      }
    }
  }, [actionStatus, history]);

  useEffect(() => {
    
  }, [preorderEventDetails, loading]);

  const handleGoBack = () => {
    history.push('/admin/preorder-events');
  };

  // Check if all items have stock = 0 to allow archiving
  const canArchive = useMemo(() => {
    if (!preorderEventDetails?.items) return false;
    return preorderEventDetails.items.every(item => item.stock === 0);
  }, [preorderEventDetails?.items]);

  const handleArchive = () => {
    // Validate that all items have stock = 0
    if (!canArchive) {
      setConfirmDialog({
        open: true,
        title: t('preorderEventDetails.cannot-archive-title'),
        message: t('preorderEventDetails.cannot-archive-message'),
        confirmText: t('common.ok'),
        onConfirm: () => {
          setConfirmDialog(prev => ({ ...prev, open: false }));
        }
      });
      return;
    }

    setConfirmDialog({
      open: true,
      title: t('preorderEventDetails.confirm-archive-title'),
      message: t('preorderEventDetails.confirm-archive-message'),
      confirmText: t('preorderEventDetails.archive'),
      onConfirm: () => {
        if (preorderId) {
          dispatch(archivePreorderEvent(parseInt(preorderId)));
        }
        setConfirmDialog(prev => ({ ...prev, open: false }));
      }
    });
  };

  const handleClosePreorder = () => {
    setConfirmDialog({
      open: true,
      title: t('preorderEventDetails.confirm-close-title'),
      message: t('preorderEventDetails.confirm-close-message'),
      confirmText: t('preorderEventDetails.continue'),
      onConfirm: () => {
        if (preorderId) {
          dispatch(getRecommendedClosedPreorder(parseInt(preorderId)));
        }
        setConfirmDialog(prev => ({ ...prev, open: false }));
      }
    });
  }

  // Location filter handlers removed.

  const handleUpdateDetails = () => {
    setUpdateDialogOpen(true);
  };

  const handleUpdateDialogClose = () => {
    setUpdateDialogOpen(false);
  };

  const handleUpdateConfirm = (updateData: {
    archived: boolean;
    name: string;
    lastPreorderDate: string;
    releaseDate: string;
  }) => {
    if (preorderId) {
      dispatch(updatePreorderEventDetails(parseInt(preorderId), updateData));
      setUpdateDialogOpen(false);
    }
  };

  const handleAddItems = () => {
    setAddItemsDialogOpen(true);
  };

  const handleAddItemsDialogClose = () => {
    setAddItemsDialogOpen(false);
  };

  const handleEditItem = (item: any) => {
    setSelectedItem(item);
    setEditItemDialogOpen(true);
  };

  const handleOpenItemMetrics = () => {
    setMetricsVendorFilter(null);
    setItemMetricsModalOpen(true);
  };

  const getItemVendorBreakdown = (itemId: number) => {
    if (!preorderEventDetails?.metrics) return [];
    return preorderEventDetails.metrics
      .map((vm: any) => {
        const found = vm.items?.find((i: any) => i.id === itemId);
        return found ? { vendorId: vm.vendorId, vendorName: vm.vendorName, quantity: found.totalSoldQuantity || 0 } : null;
      })
      .filter(Boolean);
  };

  const getFilteredMetricsRows = () => {
    if (!preorderEventDetails?.items) return [];
    return preorderEventDetails.items.flatMap((item: any) =>
      getItemVendorBreakdown(item.id)
        .filter((row: any) => metricsVendorFilter === null || row.vendorId === metricsVendorFilter)
        .map((row: any, idx: number) => ({ key: `${item.id}-${idx}`, name: item.name, vendorName: row.vendorName, quantity: row.quantity }))
    );
  };

  const handleEditQuantity = (item: any) => {
    setSelectedItem(item);
    setEditQuantityDialogOpen(true);
  };

  const handleEditPrice = (item: any) => {
    setSelectedItem(item);
    setEditPriceDialogOpen(true);
  };

  const handleEditItemDialogClose = () => {
    setEditItemDialogOpen(false);
    setSelectedItem(null);
  };

  const handleEditQuantityDialogClose = (shouldRefresh: boolean = false) => {
    setEditQuantityDialogOpen(false);
    setSelectedItem(null);
    
    // Only refresh if requested (successful operations)
    if (shouldRefresh && preorderId) {
      dispatch(getPreorderEventDetails(parseInt(preorderId)));
    }
  };

  const handleEditPriceDialogClose = (shouldRefresh: boolean = false) => {
    setEditPriceDialogOpen(false);
    setSelectedItem(null);
    
    // Only refresh if requested (successful operations)
    if (shouldRefresh && preorderId) {
      dispatch(getPreorderEventDetails(parseInt(preorderId)));
    }
  };

  const handleItemUpdate = (updateData: { 
    id: number; 
    name: string; 
    code?: string;
    newCode?: string;
    costPrice?: number;
    minimumQuantity?: number;
    notes?: string;
    labelValuesIdsToAdd?: string;
    labelValuesIdsToRemove?: string;
    price?: number;
    weightPerUnit?: number;
    isFeature?: boolean;
    isPublished?: boolean;
    maxSellQuantity?: number;
    isConversionEnabled?: boolean;
    conversionQuantity?: number;
    itemCodeForConversion?: string;
    imagesIdToRemove?: string;
    files?: { fid: number; file: File }[];
  }) => {
    // Dispatch Redux action to update the item details and refresh the data
    if (preorderId) {
      dispatch(updatePreorderItemDetails(updateData, parseInt(preorderId)));
    }
    
    setEditItemDialogOpen(false);
    setSelectedItem(null);
  };

  const handleQuantityUpdate = (updateData: { 
    id: number; 
    totalQuantity: number; 
  }) => {
    // Dispatch Redux action to update the item quantity and refresh the data
    if (preorderId) {
      dispatch(updatePreorderItemQuantity(updateData.id, updateData.totalQuantity, parseInt(preorderId)));
    }
    
    setEditQuantityDialogOpen(false);
    setSelectedItem(null);
  };

  const handlePriceUpdate = (updateData: {
    id: number;
    sellPrice: number;
    discountPercentage?: number;
    discountQuantity?: number;
  }) => {
    // Handle price update logic here - you can dispatch an action or call an API
    // For now, just close the dialog and refresh data
    if (preorderId) {
      dispatch(getPreorderEventDetails(parseInt(preorderId)));
    }
    
    setEditPriceDialogOpen(false);
    setSelectedItem(null);
  };

  // Distribution modal handlers
  const handleDistributionModalClose = () => {
    dispatch(clearSuggestedPreorderCloseDistribution());
  };

  const handleDistributionConfirm = (payload: PreorderCloseEventRequest) => {
    dispatch(closePreorderEvent(payload));
    // Refresh the details after closing
    if (preorderId) {
      dispatch(getPreorderEventDetails(parseInt(preorderId)));
    }
  };

  const handleViewOrders = () => {
    if (preorderId) {
      history.push('/admin/preorders', { eventId: parseInt(preorderId) });
    }
  };

  const handleBulkPublish = () => {
    setConfirmDialog({
      open: true,
      title: t('preorderEventDetails.confirm-publish-all-title'),
      message: t('preorderEventDetails.confirm-publish-all-message', { count: preorderEventDetails?.items.length || 0 }),
      confirmText: t('preorderEventDetails.publish-all'),
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        await executeBulkPublishOperation(true);
      }
    });
  };

  const handleBulkUnpublish = () => {
    setConfirmDialog({
      open: true,
      title: t('preorderEventDetails.confirm-unpublish-all-title'),
      message: t('preorderEventDetails.confirm-unpublish-all-message', { count: preorderEventDetails?.items.length || 0 }),
      confirmText: t('preorderEventDetails.unpublish-all'),
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        await executeBulkPublishOperation(false);
      }
    });
  };

  const executeBulkPublishOperation = (isPublished: boolean) => {
    if (!preorderId) return;
    dispatch(bulkPublishPreorderItems(parseInt(preorderId), isPublished));
  };

  const calculateSummaryData = () => {
    if (!preorderEventDetails) return {
      soldItems: 0,
      stockItems: 0,
      totalItems: 0,
      sales: 0,
      shipments: 0
    };

    // Metrics array contains vendor-specific sold quantities and prices
    const metrics = preorderEventDetails.metrics || [];

    let relevantMetricsItems: any[] = [];
    
    // Priority: If items are selected, filter by items across all vendors
    if (selectedItemIds.length > 0) {
      metrics.forEach((vm: any) => {
        if (Array.isArray(vm.items)) {
          const filteredItems = vm.items.filter((item: any) => selectedItemIds.includes(item.id));
          relevantMetricsItems = relevantMetricsItems.concat(filteredItems);
        }
      });
    }
    // Otherwise, if vendors are selected, filter by vendors
    else if (selectedVendorIds.length > 0) {
      metrics.forEach((vm: any) => {
        if (selectedVendorIds.includes(vm.vendorId) && Array.isArray(vm.items)) {
          relevantMetricsItems = relevantMetricsItems.concat(vm.items);
        }
      });
    }

    const soldItems = relevantMetricsItems.reduce((sum, mi: any) => sum + (mi.totalSoldQuantity || 0), 0);
    const sales = relevantMetricsItems.reduce((sum, mi: any) => sum + (mi.totalPrice || 0), 0);

    // Stock calculation
    let stockItems = 0;
    if (selectedItemIds.length > 0) {
      // If items are selected, only count stock for selected items
      stockItems = preorderEventDetails.items
        .filter((it: any) => selectedItemIds.includes(it.id))
        .reduce((sum: number, it: any) => sum + (it.stock || 0), 0);
    } else {
      // Otherwise count all stock
      stockItems = preorderEventDetails.items
        .reduce((sum: number, it: any) => sum + (it.stock || 0), 0);
    }

    const totalItems = soldItems + stockItems;
    const shipments = 0; // paused feature

    return { soldItems, stockItems, totalItems, sales, shipments };
  };

  // Derive list of vendors from metrics for selection UI
  const availableVendors = useMemo(() => {
    if (!preorderEventDetails || !preorderEventDetails.metrics) return [];
    return preorderEventDetails.metrics.map((m: any) => ({ id: m.vendorId, name: m.vendorName }));
  }, [preorderEventDetails]);

  const handleVendorToggle = (vendorId: number) => {
    // Clear item selection when selecting vendors
    if (selectedItemIds.length > 0) {
      setSelectedItemIds([]);
    }
    
    setSelectedVendorIds(prev => {
      if (prev.includes(vendorId)) {
        return prev.filter(id => id !== vendorId);
      }
      return [...prev, vendorId];
    });
  };

  const handleItemToggle = (itemId: number) => {
    // Clear vendor selection when selecting items
    if (selectedVendorIds.length > 0) {
      setSelectedVendorIds([]);
    }
    
    setSelectedItemIds(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      }
      return [...prev, itemId];
    });
  };

  // Default select all vendors when data first available (only once)
  useEffect(() => {
    if (availableVendors.length > 0 && !hasInitializedVendors) {
      setSelectedVendorIds(availableVendors.map(v => v.id));
      setHasInitializedVendors(true);
    }
  }, [availableVendors, hasInitializedVendors]);

  if (loading === 'GET_PREORDER_EVENT_DETAILS') {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={60} />
      </div>
    );
  }

  if (!preorderEventDetails && loading !== 'GET_PREORDER_EVENT_DETAILS') {
    return (
      <div className={classes.errorContainer}>
        <Typography variant="h6">
          {t('preorderEventDetails.event-not-found')}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
          style={{ marginTop: 16 }}
        >
          {t('common.go-back')}
        </Button>
      </div>
    );
  }

  if (!preorderEventDetails) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={60} />
      </div>
    );
  }

  const summaryData = calculateSummaryData();

  return (
    <div className={classes.container}>
      {/* Header */}
      <div className={classes.header}>
        <div>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
            className={classes.backButton}
          >
            {t('common.go-back')}
          </Button>
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <Typography variant="h4" className={classes.title}>
              {preorderEventDetails.name}
            </Typography>
            {preorderEventDetails.archived ? (
              <Chip
                label={t('common.closed')}
                className={`${classes.statusChip} ${classes.closedStatus}`}
              />
            ) : null }
            {preorderEventDetails.closed ? (
              <Chip
                label={t('common.closed')}
                className={`${classes.statusChip} ${classes.closedStatus}`}
              />
            ) : null }
            {!preorderEventDetails.archived && !preorderEventDetails.closed ? (
              <Chip

                label={t('common.active')}
                className={`${classes.statusChip} ${classes.activeStatus}`}
              />
            ) : null }
          </div>
        </div>
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {!preorderEventDetails.archived ? (
              <Tooltip 
                title={!canArchive ? String(t('preorderEventDetails.cannot-archive-tooltip')) : ''}
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    className={`${classes.actionButton} ${canArchive ? classes.archiveButton : classes.disabledButton}`}
                    onClick={handleArchive}
                    size="small"
                    disabled={!canArchive}
                  >
                    {t('preorderEventDetails.archive-preorder')}
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                variant="contained"
                className={`${classes.actionButton} ${classes.disabledButton}`}
                disabled
                size="small"
              >
                {t('preorderEventDetails.already-archived')}
              </Button>
            )}
            <Button
                variant="contained"
                className={`${classes.actionButton} ${classes.closeButton}`}
                onClick={handleClosePreorder}
                size="small"
              >
                {t('preorderEventDetails.close-preorder')}
              </Button>
            <Button
              variant="contained"
              className={`${classes.actionButton} ${classes.disabledButton}`}
              disabled
              size="small"
            >
              {t('preorderEventDetails.create-order')}
            </Button>
            <Button
              variant="contained"
              className={classes.actionButton}
              onClick={handleViewOrders}
              size="small"
            >
              {t('preorderEventDetails.view-orders')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenItemMetrics}
              size="small"
              startIcon={<AssessmentIcon />}
            >
              {t('preorderEventDetails.item-metrics')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleBulkPublish}
              size="small"
              disabled={loading === 'BULK_PUBLISH_PREORDER_ITEMS' || !preorderEventDetails?.items?.length}
            >
              {loading === 'BULK_PUBLISH_PREORDER_ITEMS' ? 
                t('preorderEventDetails.publishing') : 
                t('preorderEventDetails.publish-all')}
            </Button>
            <Button
              variant="contained"
              color="default"
              onClick={handleBulkUnpublish}
              size="small"
              disabled={loading === 'BULK_PUBLISH_PREORDER_ITEMS' || !preorderEventDetails?.items?.length}
            >
              {loading === 'BULK_PUBLISH_PREORDER_ITEMS' ? 
                t('preorderEventDetails.unpublishing') : 
                t('preorderEventDetails.unpublish-all')}
            </Button>
          </div>
        </div>
      </div>

      {/* Second Section - Metrics Cards */}
      <div className={classes.summarySection}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} lg={2}>
            <Paper className={classes.summaryCard}>
              <Typography className={classes.summaryValue}>
                {summaryData.soldItems}
              </Typography>
              <Typography className={classes.summaryLabel}>
                {t('preorderEventDetails.sold-items')}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <Paper className={classes.summaryCard}>
              <Typography className={classes.summaryValue}>
                {summaryData.stockItems}
              </Typography>
              <Typography className={classes.summaryLabel}>
                {t('preorderEventDetails.stock')}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <Paper className={classes.summaryCard}>
              <Typography className={classes.summaryValue}>
                {summaryData.totalItems}
              </Typography>
              <Typography className={classes.summaryLabel}>
                {t('preorderEventDetails.total-items')}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <Paper className={classes.summaryCard}>
              <Typography className={classes.summaryValue}>
                {summaryData.shipments}
              </Typography>
              <Typography className={classes.summaryLabel}>
                {t('preorderEventDetails.shipments')}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <Paper className={classes.summaryCard}>
              <Typography className={classes.summaryValue}>
                ₡{summaryData.sales.toLocaleString()}
              </Typography>
              <Typography className={classes.summaryLabel}>
                {t('preorderEventDetails.sales')}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </div>

      {/* Third Section - Vendor Cards (multi-select) */}
      <div className={classes.summarySection}>
        {/* Select All / Clear buttons removed as per request */}
        <Grid container spacing={2} style={{ marginBottom: 32, maxWidth: 600 }}>
          {availableVendors.map(v => (
            <Grid item xs={12} sm={4} md={4} key={v.id}>
              <Paper
                className={`${classes.summaryCard} ${classes.locationCardSmall} ${selectedVendorIds.includes(v.id) ? classes.locationCardSelected : classes.locationCardUnselected}`}
                onClick={() => handleVendorToggle(v.id)}
              >
                <Typography className={classes.summaryValue}>
                  {v.name}
                </Typography>
                <Typography className={classes.summaryLabel}>
                  {t('preorderEventDetails.vendor-id')}: {v.id}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </div>

      {/* Fourth Section - Details and Items */}
      <div className={classes.detailsSection}>
        <div className={classes.detailsContainer}>
          {/* Left Side - Details */}
          <div className={classes.leftDetails}>
            <Paper className={classes.detailsCard}>
              <Typography variant="h6" style={{ marginBottom: 16, fontWeight: 'bold' }}>
                {t('preorderEventDetails.event-details')}
              </Typography>
              
              <div className={classes.detailItem}>
                <Typography className={classes.detailLabel}>
                  {t('preorderEvents.name')}
                </Typography>
                <Typography className={classes.detailValue}>
                  {preorderEventDetails.name}
                </Typography>
              </div>

              <div className={classes.detailItem}>
                <Typography className={classes.detailLabel}>
                  {t('preorderEvents.release-date')}
                </Typography>
                <Typography className={classes.detailValue}>
                  {Moment(preorderEventDetails.releaseDate).format('DD/MM/YYYY')}
                </Typography>
              </div>

              <div className={classes.detailItem}>
                <Typography className={classes.detailLabel}>
                  {t('preorderEvents.last-preorder-date')}
                </Typography>
                <Typography className={classes.detailValue}>
                  {Moment(preorderEventDetails.lastPreorderDate).format('DD/MM/YYYY')}
                </Typography>
              </div>

              <div style={{ marginTop: 24, textAlign: 'center', display: 'flex', gap: 16, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpdateDetails}
                  style={{ minWidth: 150 }}
                >
                  {t('preorderEventDetails.update-details')}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleAddItems}
                  style={{ minWidth: 150 }}
                >
                  {t('preorderEventDetails.add-items')}
                </Button>
              </div>
            </Paper>
          </div>

          {/* Right Side - Items */}
          <div className={classes.rightItems}>
            <Paper className={classes.itemsTable}>
              <Typography variant="h6" style={{ padding: 16, fontWeight: 'bold' }}>
                {t('preorderEventDetails.items')} ({preorderEventDetails.items.length})
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead className={classes.tableHeader}>
                    <TableRow>
                      <TableCell padding="checkbox" style={{ width: 50 }}>
                        {/* Select All Checkbox */}
                      </TableCell>
                      <TableCell>{t('common.name')}</TableCell>
                      <TableCell>{t('common.sku')}</TableCell>
                      <TableCell align="right">{t('preorderEventDetails.stock')}</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preorderEventDetails.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedItemIds.includes(item.id)}
                            onChange={() => handleItemToggle(item.id)}
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>{item.sku}</TableCell>
                        <TableCell align="right">{item.stock}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleEditItem(item)}
                            title={t('preorderEventDetails.edit-item')}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEditQuantity(item)}
                            title={t('preorderEventDetails.edit-quantity')}
                          >
                            <FormatListNumberedIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEditPrice(item)}
                            title={t('preorderEventDetails.edit-price')}
                          >
                            <AttachMoneyIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={t('common.cancel')}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        confirmColor="secondary"
      />

      {/* Update Details Dialog */}
      <UpdatePreorderDetailsDialog
        open={updateDialogOpen}
        onClose={handleUpdateDialogClose}
        onUpdate={handleUpdateConfirm}
        preorderEventDetails={preorderEventDetails}
        isLoading={loading === 'UPDATE_PREORDER_EVENT_DETAILS'}
      />

      {/* Add Items Dialog */}
      <AddItemsDialog
        open={addItemsDialogOpen}
        onClose={handleAddItemsDialogClose}
        preorderEventId={preorderId ? parseInt(preorderId) : undefined}
        preorderVendorId={preorderEventDetails?.preorderVendorId}
      />

      {/* Edit Item Dialog */}
      <EditItemDialog
        open={editItemDialogOpen}
        onClose={handleEditItemDialogClose}
        onUpdate={handleItemUpdate}
        item={selectedItem}
        isLoading={loading === 'UPDATE_PREORDER_ITEM_DETAILS'}
      />

      {/* Edit Quantity Dialog */}
      <EditQuantityDialog
        open={editQuantityDialogOpen}
        onClose={handleEditQuantityDialogClose}
        onUpdate={handleQuantityUpdate}
        item={selectedItem}
        isLoading={false}
      />

      {/* Edit Price Dialog */}
      <EditPriceDialog
        open={editPriceDialogOpen}
        onClose={handleEditPriceDialogClose}
        onUpdate={handlePriceUpdate}
        item={selectedItem}
        isLoading={false}
      />

      {/* Suggested Distribution Modal */}
      <PreorderSuggestedDistributionModal
        open={suggestedPreorderCloseDistribution !== undefined}
        onClose={handleDistributionModalClose}
        onConfirm={handleDistributionConfirm}
      />

      {/* Action Alert for Toast Messages */}
      <ActionAlert />

      {/* Item Metrics Modal */}
      <Modal
        open={itemMetricsModalOpen}
        onClose={() => setItemMetricsModalOpen(false)}
        aria-labelledby="item-metrics-modal"
      >
        <Paper className={classes.metricsModalPaper}>
          <div className={classes.metricsModalHeader}>
            <Typography variant="h6" style={{ fontWeight: 'bold' }}>
              {t('preorderEventDetails.item-metrics')}
            </Typography>
            <PreorderMetricsPrintButton
              rows={getFilteredMetricsRows()}
              preorderName={preorderEventDetails?.name || ''}
              vendorLabel={metricsVendorFilter
                ? availableVendors.find(v => v.id === metricsVendorFilter)?.name || ''
                : t('common.all')
              }
            />
          </div>

          {/* Vendor filter */}
          <div className={classes.metricsModalFilter}>
            <Typography variant="caption" style={{ alignSelf: 'center', marginRight: 8, fontWeight: 'bold' }}>
              {t('preorderEventDetails.vendor')}:
            </Typography>
            <Chip
              label={t('common.all')}
              color={metricsVendorFilter === null ? 'primary' : 'default'}
              onClick={() => setMetricsVendorFilter(null)}
              clickable
            />
            {availableVendors.map(v => (
              <Chip
                key={v.id}
                label={v.name}
                color={metricsVendorFilter === v.id ? 'primary' : 'default'}
                onClick={() => setMetricsVendorFilter(v.id)}
                clickable
              />
            ))}
          </div>

          <TableContainer>
            <Table size="small">
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  <TableCell><strong>{t('common.name')}</strong></TableCell>
                  <TableCell><strong>{t('preorderEventDetails.vendor')}</strong></TableCell>
                  <TableCell align="right"><strong>{t('preorderEventDetails.sold-items')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredMetricsRows().length > 0
                  ? getFilteredMetricsRows().map((row) => (
                      <TableRow key={row.key}>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.vendorName}</TableCell>
                        <TableCell align="right">{row.quantity}</TableCell>
                      </TableRow>
                    ))
                  : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="body2" color="textSecondary">
                          {t('preorderEventDetails.no-sales-data')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                }
              </TableBody>
            </Table>
          </TableContainer>

          <div className={classes.metricsModalActions}>
            <Button variant="contained" color="primary" onClick={() => setItemMetricsModalOpen(false)}>
              {t('common.close')}
            </Button>
          </div>
        </Paper>
      </Modal>
    </div>
  );
};

export default PreorderEventDetails;